import React, { useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch, ApiError } from './api';
import EstadoErro from './EstadoErro';
import EstadoVazio from './EstadoVazio';
import EstadoCarregando from './EstadoCarregando';
import './VerificacaoMarca.css';

const TIPOS_BUSCA = [
  { valor: 'exata', descricao: 'Exata (identidade total)' },
  { valor: 'fonetica', descricao: 'Fonética (som similar)' },
  { valor: 'radical', descricao: 'Radical (mesma raiz)' },
  { valor: 'silabica', descricao: 'Silábica (sílabas similares)' },
  { valor: 'aproximada', descricao: 'Aproximada (ampla)' },
  { valor: 'grafica', descricao: 'Gráfica (aparência similar)' },
  { valor: 'ideologica', descricao: 'Ideológica (conceito similar)' },
];

const CLASSES_NICE = [
  { numero: 1, descricao: 'Produtos químicos' },
  { numero: 3, descricao: 'Cosméticos e produtos de limpeza' },
  { numero: 9, descricao: 'Aparelhos e instrumentos científicos' },
  { numero: 25, descricao: 'Vestuário, calçados e chapelaria' },
  { numero: 35, descricao: 'Publicidade e gestão de negócios comerciais' },
  { numero: 42, descricao: 'Serviços científicos e tecnológicos' },
];

const TAMANHO_PAGINA_HISTORICO = 10;

// Relatório PDF assíncrono (SEF-49 / todo.md §7.4): POST dispara a geração,
// GET é consultado em polling até status terminal.
const RELATORIO_POLL_INTERVALO_MS = 2500;
const RELATORIO_POLL_MAX_TENTATIVAS = 48; // ~2 min de espera antes de desistir

// Mensagens amigáveis por código de erro do backend (contrato { error: { code } }).
const MENSAGENS_ERRO = {
  RATE_LIMITED:
    'Você atingiu o limite de verificações por hora deste escritório (100/hora). ' +
    'Aguarde alguns minutos e tente novamente.',
  VALIDATION_ERROR: 'Verifique os campos do formulário e tente novamente.',
  NOT_FOUND: 'Verificação não encontrada ou indisponível para este escritório.',
};

function mensagemDeErro(erro, padrao) {
  if (erro instanceof ApiError && erro.code && MENSAGENS_ERRO[erro.code]) {
    return MENSAGENS_ERRO[erro.code];
  }
  return padrao;
}

function formatarDataHora(iso) {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return data.toLocaleString('pt-BR');
}

function rotuloTipoBusca(valor) {
  return TIPOS_BUSCA.find((t) => t.valor === valor)?.descricao.split(' ')[0] || valor;
}

// Geração + polling + download autenticado do relatório PDF (todo.md §7.4).
// O mesmo componente é reutilizado ao trocar de verificação, então o estado é
// reiniciado quando `verificacaoId` muda e os timers são limpos no unmount.
function useRelatorioPdf(verificacaoId) {
  // 'idle' | 'gerando' (POST + polling em curso) | 'concluido' | 'erro'
  const [status, setStatus] = useState('idle');
  const [erro, setErro] = useState(null);
  const [baixando, setBaixando] = useState(false);
  const timerRef = useRef(null);
  const ativoRef = useRef(true);
  // verificacaoId vigente — descarta polling em voo de uma verificação antiga
  // quando o usuário troca de verificação (SEF-142).
  const verificacaoIdRef = useRef(verificacaoId);

  useEffect(() => {
    ativoRef.current = true;
    return () => {
      ativoRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    verificacaoIdRef.current = verificacaoId;
    setStatus('idle');
    setErro(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [verificacaoId]);

  const consultar = useCallback(
    async (tentativa) => {
      if (!ativoRef.current || verificacaoIdRef.current !== verificacaoId) return;
      try {
        const resp = await apiFetch(`/api/marcas/verificacoes/${verificacaoId}/relatorio`);
        if (!ativoRef.current || verificacaoIdRef.current !== verificacaoId) return;
        if (resp?.status === 'concluido') {
          setStatus('concluido');
          return;
        }
        if (resp?.status === 'erro') {
          setStatus('erro');
          setErro('A geração do relatório falhou. Tente novamente.');
          return;
        }
        if (tentativa >= RELATORIO_POLL_MAX_TENTATIVAS) {
          setStatus('erro');
          setErro('A geração está demorando mais que o esperado. Tente novamente em instantes.');
          return;
        }
        timerRef.current = setTimeout(
          () => consultar(tentativa + 1),
          RELATORIO_POLL_INTERVALO_MS,
        );
      } catch (e) {
        if (!ativoRef.current || verificacaoIdRef.current !== verificacaoId) return;
        setStatus('erro');
        setErro(mensagemDeErro(e, 'Não foi possível consultar o status do relatório.'));
      }
    },
    [verificacaoId],
  );

  const gerar = useCallback(async () => {
    setStatus('gerando');
    setErro(null);
    try {
      await apiFetch(`/api/marcas/verificacoes/${verificacaoId}/relatorio`, {
        method: 'POST',
      });
      if (!ativoRef.current || verificacaoIdRef.current !== verificacaoId) return;
      consultar(1);
    } catch (e) {
      if (!ativoRef.current || verificacaoIdRef.current !== verificacaoId) return;
      setStatus('erro');
      setErro(mensagemDeErro(e, 'Não foi possível solicitar o relatório. Tente novamente.'));
    }
  }, [verificacaoId, consultar]);

  const baixar = useCallback(async () => {
    setBaixando(true);
    setErro(null);
    try {
      const blob = await apiFetch(
        `/api/marcas/verificacoes/${verificacaoId}/relatorio/arquivo`,
        { responseType: 'blob' },
      );
      // descarta o download antigo se o usuário já trocou de verificação (SEF-142)
      if (!ativoRef.current || verificacaoIdRef.current !== verificacaoId) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-verificacao-${verificacaoId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (!ativoRef.current || verificacaoIdRef.current !== verificacaoId) return;
      setErro(mensagemDeErro(e, 'Não foi possível baixar o PDF. Tente novamente.'));
    } finally {
      if (ativoRef.current && verificacaoIdRef.current === verificacaoId) setBaixando(false);
    }
  }, [verificacaoId]);

  return { status, erro, baixando, gerar, baixar };
}

// Painel de resultado reaproveitado tanto pela verificação recém-feita quanto
// pelo detalhe de uma consulta histórica (mesmo shape de resposta — 7.1/7.3).
function ResultadoVerificacao({ resultado }) {
  const disponivel = resultado.disponivel;
  const similares = resultado.marcasSimilares || [];
  const relatorio = useRelatorioPdf(resultado.id);

  return (
    <div className={`resultado-container ${disponivel ? 'disponivel' : 'indisponivel'}`}>
      <div className="resultado-header">
        <h3>{disponivel ? 'Marca Potencialmente Disponível' : 'Possível Conflito Detectado'}</h3>
        <span className="timestamp">{formatarDataHora(resultado.timestamp)}</span>
      </div>

      <div className="resultado-detalhes">
        {resultado.nomeMarca && (
          <div className="detalhe-item">
            <span className="detalhe-label">Marca:</span>
            <span className="detalhe-valor">{resultado.nomeMarca}</span>
          </div>
        )}

        <div className="detalhe-item">
          <span className="detalhe-label">Método de Busca:</span>
          <span className="detalhe-valor">{rotuloTipoBusca(resultado.tipoBusca)}</span>
        </div>

        {resultado.classeNice != null && (
          <div className="detalhe-item">
            <span className="detalhe-label">Classe Nice:</span>
            <span className="detalhe-valor">{resultado.classeNice}</span>
          </div>
        )}

        <div className="detalhe-item">
          <span className="detalhe-label">Índice de Similaridade:</span>
          <span className="detalhe-valor">{resultado.similaridadePercentual}%</span>
        </div>
      </div>

      <p className="resultado-mensagem">{resultado.mensagem}</p>

      {similares.length > 0 && (
        <div className="marcas-similares">
          <h4>Potenciais Marcas Colidentes:</h4>
          <table className="tabela-similares">
            <thead>
              <tr>
                <th>Marca</th>
                <th>Processo</th>
                <th>Classe</th>
                <th>Situação</th>
                <th>Titular</th>
                <th>Similaridade</th>
              </tr>
            </thead>
            <tbody>
              {similares.map((m, i) => (
                <tr key={`${m.processo}-${i}`}>
                  <td>{m.marca}</td>
                  <td>{m.processo}</td>
                  <td>{m.classe}</td>
                  <td>{m.situacao}</td>
                  <td>{m.titular}</td>
                  <td>{m.similaridade}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="acoes-adicionais">
        <button
          type="button"
          className="action-button"
          onClick={relatorio.gerar}
          disabled={
            !resultado.id || relatorio.status === 'gerando' || relatorio.baixando
          }
        >
          {relatorio.status === 'gerando' ? 'Gerando relatório…' : 'Gerar relatório PDF'}
        </button>
        {relatorio.status === 'concluido' && (
          <button
            type="button"
            className="action-button"
            onClick={relatorio.baixar}
            disabled={relatorio.baixando}
          >
            {relatorio.baixando ? 'Baixando…' : 'Baixar PDF'}
          </button>
        )}
      </div>

      {relatorio.status === 'gerando' && (
        <p className="relatorio-status" role="status">
          Gerando o relatório PDF da verificação. Isso pode levar alguns segundos…
        </p>
      )}
      {relatorio.status === 'concluido' && !relatorio.erro && (
        <p className="relatorio-status" role="status">
          Relatório pronto. Clique em <strong>Baixar PDF</strong> para salvar o arquivo.
        </p>
      )}
      {relatorio.erro && (
        <p className="relatorio-erro" role="alert">
          {relatorio.erro}
        </p>
      )}
    </div>
  );
}

function VerificacaoMarca() {
  const [formData, setFormData] = useState({
    nomeMarca: '',
    classeNice: '',
    tipoMarca: 'nominativa',
    tipoBusca: 'aproximada',
    especificacoes: '',
    requerente: '',
    comentariosAdicionais: '',
  });
  const [resultado, setResultado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erroVerificacao, setErroVerificacao] = useState(null);
  const [filtroAvancado, setFiltroAvancado] = useState(false);

  // Histórico (GET /api/marcas/verificacoes — paginado + filtros).
  const [filtros, setFiltros] = useState({
    nomeMarca: '',
    classeNice: '',
    dataInicio: '',
    dataFim: '',
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtros);
  const [paginaHistorico, setPaginaHistorico] = useState(1);
  const [historico, setHistorico] = useState(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(true);
  const [erroHistorico, setErroHistorico] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true);
    setErroHistorico(null);
    try {
      const params = new URLSearchParams();
      if (filtrosAplicados.nomeMarca.trim()) {
        params.set('nomeMarca', filtrosAplicados.nomeMarca.trim());
      }
      if (filtrosAplicados.classeNice) {
        params.set('classeNice', String(filtrosAplicados.classeNice));
      }
      if (filtrosAplicados.dataInicio) params.set('dataInicio', filtrosAplicados.dataInicio);
      if (filtrosAplicados.dataFim) params.set('dataFim', filtrosAplicados.dataFim);
      params.set('page', String(paginaHistorico - 1)); // request é base 0
      params.set('size', String(TAMANHO_PAGINA_HISTORICO));
      const resp = await apiFetch(`/api/marcas/verificacoes?${params.toString()}`);
      setHistorico(resp);
    } catch (erro) {
      setErroHistorico(mensagemDeErro(erro, 'Não foi possível carregar o histórico de verificações.'));
    } finally {
      setCarregandoHistorico(false);
    }
  }, [filtrosAplicados, paginaHistorico]);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  const aplicarFiltros = (e) => {
    e.preventDefault();
    setPaginaHistorico(1);
    setFiltrosAplicados(filtros);
  };

  const limparFiltros = () => {
    const vazio = { nomeMarca: '', classeNice: '', dataInicio: '', dataFim: '' };
    setFiltros(vazio);
    setFiltrosAplicados(vazio);
    setPaginaHistorico(1);
  };

  const verificarDisponibilidade = async (e) => {
    e.preventDefault();
    const nome = formData.nomeMarca.trim();
    if (!nome) return;
    if (!formData.classeNice) {
      // Classe Nice é obrigatória no contrato (1–45). Abre os filtros
      // avançados para o usuário escolher.
      setFiltroAvancado(true);
      setErroVerificacao('Selecione a Classe Nice (campo obrigatório para a consulta).');
      return;
    }

    setCarregando(true);
    setResultado(null);
    setErroVerificacao(null);

    const corpo = {
      nomeMarca: nome,
      classeNice: Number(formData.classeNice),
      tipoMarca: formData.tipoMarca,
      tipoBusca: formData.tipoBusca,
    };
    if (formData.especificacoes.trim()) corpo.especificacoes = formData.especificacoes.trim();
    if (formData.requerente.trim()) corpo.requerente = formData.requerente.trim();
    if (formData.comentariosAdicionais.trim()) {
      corpo.comentariosAdicionais = formData.comentariosAdicionais.trim();
    }

    try {
      const resp = await apiFetch('/api/marcas/verificar-disponibilidade', {
        method: 'POST',
        body: corpo,
      });
      setResultado({ ...resp, nomeMarca: nome });
      // Recarrega a primeira página do histórico para refletir a nova consulta.
      if (paginaHistorico === 1) {
        carregarHistorico();
      } else {
        setPaginaHistorico(1);
      }
    } catch (erro) {
      setErroVerificacao(
        mensagemDeErro(erro, 'Não foi possível concluir a verificação. Tente novamente.'),
      );
    } finally {
      setCarregando(false);
    }
  };

  const abrirDetalhe = async (id) => {
    setCarregandoDetalhe(true);
    setErroVerificacao(null);
    setResultado(null);
    try {
      const resp = await apiFetch(`/api/marcas/verificacoes/${id}`);
      setResultado(resp);
    } catch (erro) {
      setErroVerificacao(
        mensagemDeErro(erro, 'Não foi possível abrir o detalhe desta verificação.'),
      );
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  const itens = historico?.data || [];
  const totalPaginas = historico?.metadata?.totalPages || 0;

  return (
    <div className="verificacao-marca-container especialista">
      <div className="verificacao-marca-content">
        <div className="verificacao-cabecalho">
          <h1>Consulta de Disponibilidade de Marcas Registráveis</h1>
        </div>

        <div className="conteudo-vertical">
          <div className="secao formulario">
            <h2>Formulário de Consulta</h2>
            <form onSubmit={verificarDisponibilidade} className="verificacao-marca-form">
              <div className="input-group">
                <label htmlFor="nomeMarca">Nome da Marca*</label>
                <input
                  type="text"
                  id="nomeMarca"
                  name="nomeMarca"
                  value={formData.nomeMarca}
                  onChange={handleInputChange}
                  placeholder="Digite o nome da marca"
                  maxLength={100}
                  required
                />
              </div>

              <div className="input-group">
                <label htmlFor="tipoBusca">Método de Busca</label>
                <select
                  id="tipoBusca"
                  name="tipoBusca"
                  value={formData.tipoBusca}
                  onChange={handleInputChange}
                  className="tipo-busca-select"
                >
                  {TIPOS_BUSCA.map((tipo) => (
                    <option key={tipo.valor} value={tipo.valor}>
                      {tipo.descricao}
                    </option>
                  ))}
                </select>
                <div className="tipo-busca-info">
                  {formData.tipoBusca === 'exata' && (
                    <small>Busca apenas por marcas idênticas ao termo digitado.</small>
                  )}
                  {formData.tipoBusca === 'fonetica' && (
                    <small>Busca por marcas com pronúncia similar, independente da grafia.</small>
                  )}
                  {formData.tipoBusca === 'radical' && (
                    <small>Busca por marcas que compartilham a mesma raiz ou origem etimológica.</small>
                  )}
                  {formData.tipoBusca === 'silabica' && (
                    <small>Busca por marcas que compartilham sílabas ou estrutura silábica similar.</small>
                  )}
                  {formData.tipoBusca === 'aproximada' && (
                    <small>Busca ampla combinando múltiplos métodos para encontrar potenciais colidências.</small>
                  )}
                  {(formData.tipoBusca === 'grafica' || formData.tipoBusca === 'ideologica') && (
                    <small>
                      Este método ainda não está disponível nesta versão — a consulta retornará
                      apenas um aviso explicativo (previsto para uma fase futura).
                    </small>
                  )}
                </div>
              </div>

              <div className="toggle-container">
                <button
                  type="button"
                  className="toggle-button"
                  onClick={() => setFiltroAvancado(!filtroAvancado)}
                >
                  {filtroAvancado ? 'Ocultar Filtros Avançados' : 'Mostrar Filtros Avançados'}
                </button>
              </div>

              {filtroAvancado && (
                <div className="filtros-avancados">
                  <div className="form-row">
                    <div className="input-group">
                      <label htmlFor="classeNice">Classe Nice*</label>
                      <select
                        id="classeNice"
                        name="classeNice"
                        value={formData.classeNice}
                        onChange={handleInputChange}
                      >
                        <option value="">Selecione uma classe</option>
                        {CLASSES_NICE.map((classe) => (
                          <option key={classe.numero} value={classe.numero}>
                            {classe.numero} - {classe.descricao}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="input-group">
                      <label htmlFor="tipoMarca">Tipo de Marca</label>
                      <select
                        id="tipoMarca"
                        name="tipoMarca"
                        value={formData.tipoMarca}
                        onChange={handleInputChange}
                      >
                        <option value="nominativa">Nominativa</option>
                        <option value="figurativa">Figurativa</option>
                        <option value="mista">Mista</option>
                        <option value="tridimensional">Tridimensional</option>
                      </select>
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="especificacoes">Especificações de Produtos/Serviços</label>
                    <textarea
                      id="especificacoes"
                      name="especificacoes"
                      value={formData.especificacoes}
                      onChange={handleInputChange}
                      placeholder="Descreva os produtos ou serviços específicos"
                      rows="3"
                      maxLength={2000}
                    ></textarea>
                  </div>

                  <div className="input-group">
                    <label htmlFor="requerente">Requerente</label>
                    <input
                      type="text"
                      id="requerente"
                      name="requerente"
                      value={formData.requerente}
                      onChange={handleInputChange}
                      placeholder="Nome do cliente requerente"
                      maxLength={255}
                    />
                  </div>

                  <div className="input-group">
                    <label htmlFor="comentariosAdicionais">Comentários Adicionais</label>
                    <textarea
                      id="comentariosAdicionais"
                      name="comentariosAdicionais"
                      value={formData.comentariosAdicionais}
                      onChange={handleInputChange}
                      placeholder="Observações ou contextualização jurídica adicional"
                      rows="2"
                      maxLength={2000}
                    ></textarea>
                  </div>
                </div>
              )}

              <button type="submit" className="verificar-button" disabled={carregando}>
                {carregando ? 'Processando consulta...' : 'Verificar Disponibilidade'}
              </button>
            </form>
          </div>

          <div className="secao resultados">
            <h2>Resultado da Análise</h2>

            {(carregando || carregandoDetalhe) && (
              <EstadoCarregando mensagem="Consultando banco de dados do INPI..." inline />
            )}

            {!carregando && !carregandoDetalhe && erroVerificacao && (
              <EstadoErro mensagem={erroVerificacao} />
            )}

            {!carregando && !carregandoDetalhe && !erroVerificacao && resultado && (
              <ResultadoVerificacao resultado={resultado} />
            )}

            {!carregando && !carregandoDetalhe && !erroVerificacao && !resultado && (
              <p className="sem-historico">
                Faça uma verificação ou abra uma consulta do histórico para ver o resultado.
              </p>
            )}

            <div className="historico-consultas">
              <h3>Histórico de Verificações</h3>

              <form className="filtros-historico" onSubmit={aplicarFiltros}>
                <div className="form-row">
                  <div className="input-group">
                    <label htmlFor="filtroNomeMarca">Nome da marca</label>
                    <input
                      type="text"
                      id="filtroNomeMarca"
                      name="nomeMarca"
                      value={filtros.nomeMarca}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por nome"
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="filtroClasseNice">Classe Nice</label>
                    <select
                      id="filtroClasseNice"
                      name="classeNice"
                      value={filtros.classeNice}
                      onChange={handleFiltroChange}
                    >
                      <option value="">Todas</option>
                      {CLASSES_NICE.map((classe) => (
                        <option key={classe.numero} value={classe.numero}>
                          {classe.numero} - {classe.descricao}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="input-group">
                    <label htmlFor="filtroDataInicio">De</label>
                    <input
                      type="date"
                      id="filtroDataInicio"
                      name="dataInicio"
                      value={filtros.dataInicio}
                      onChange={handleFiltroChange}
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="filtroDataFim">Até</label>
                    <input
                      type="date"
                      id="filtroDataFim"
                      name="dataFim"
                      value={filtros.dataFim}
                      onChange={handleFiltroChange}
                    />
                  </div>
                </div>
                <div className="acoes-filtro">
                  <button type="submit" className="action-button">
                    Filtrar
                  </button>
                  <button
                    type="button"
                    className="action-button outlined"
                    onClick={limparFiltros}
                  >
                    Limpar
                  </button>
                </div>
              </form>

              {carregandoHistorico && <EstadoCarregando mensagem="Carregando histórico..." inline />}

              {!carregandoHistorico && erroHistorico && (
                <EstadoErro mensagem={erroHistorico} onRetry={carregarHistorico} />
              )}

              {!carregandoHistorico && !erroHistorico && itens.length === 0 && (
                <EstadoVazio
                  titulo="Nenhuma verificação encontrada"
                  descricao="Nenhuma consulta corresponde aos filtros selecionados."
                  icone="fa-solid fa-magnifying-glass"
                />
              )}

              {!carregandoHistorico && !erroHistorico && itens.length > 0 && (
                <>
                  <table className="tabela-historico">
                    <thead>
                      <tr>
                        <th>Marca</th>
                        <th>Classe</th>
                        <th>Tipo</th>
                        <th>Busca</th>
                        <th>Resultado</th>
                        <th>Data</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((consulta) => (
                        <tr key={consulta.id}>
                          <td>{consulta.nomeMarca}</td>
                          <td>{consulta.classeNice}</td>
                          <td>{consulta.tipoMarca}</td>
                          <td>{rotuloTipoBusca(consulta.tipoBusca)}</td>
                          <td
                            className={
                              consulta.disponivel ? 'disponivel-text' : 'indisponivel-text'
                            }
                          >
                            {consulta.disponivel
                              ? 'Possivelmente disponível'
                              : 'Possível conflito'}
                          </td>
                          <td>{formatarDataHora(consulta.timestamp)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn-acao visualizar"
                              title="Ver detalhe"
                              onClick={() => abrirDetalhe(consulta.id)}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {totalPaginas > 1 && (
                    <div className="paginacao-historico">
                      <button
                        type="button"
                        className="action-button outlined"
                        disabled={paginaHistorico <= 1}
                        onClick={() => setPaginaHistorico((p) => Math.max(1, p - 1))}
                      >
                        Anterior
                      </button>
                      <span className="paginacao-info">
                        Página {paginaHistorico} de {totalPaginas}
                      </span>
                      <button
                        type="button"
                        className="action-button outlined"
                        disabled={paginaHistorico >= totalPaginas}
                        onClick={() =>
                          setPaginaHistorico((p) => Math.min(totalPaginas, p + 1))
                        }
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerificacaoMarca;
